import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { CheckCircle2, Circle, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const RULES = [
  { t: "At least 8 characters", ok: (p) => p.length >= 8 },
  { t: "Contains a letter", ok: (p) => /[A-Za-z]/.test(p) },
  { t: "Contains a number", ok: (p) => /\d/.test(p) },
];
const inputCls = "w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm";

export default function ResetPassword() {
  const nav = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    window.history.replaceState(null, "", "/reset-password"); // keep token out of history/referrers
    if (!token) { setInvalid(true); return; }
    api.get("/auth/password-token", { params: { token } }).then(r => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const valid = RULES.every(r => r.ok(pw)) && pw === pw2;
  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password saved — please sign in");
      nav("/login", { state: { email: info.email } });
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="reset-password-page">
      <div className="w-full max-w-md ae-card p-8">
        {invalid ? (
          <div className="text-center" data-testid="reset-invalid">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-medium text-[#0F2A4A] mb-2">Link invalid or expired</h1>
            <p className="text-sm text-slate-600">Setup and reset links work once and expire after 60 minutes.</p>
            <Link to="/forgot-password" className="ae-btn-primary inline-block mt-6 text-sm" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : !info ? <div className="text-center text-slate-500 py-8">Verifying link…</div> : (
          <form onSubmit={submit} className="space-y-4">
            <ShieldCheck className="w-10 h-10 text-[#C5A059]" />
            <div className="ae-overline">{info.purpose === "setup" ? "Account setup" : "Password reset"}</div>
            <h1 className="font-heading text-3xl font-medium text-[#0F2A4A]">{info.purpose === "setup" ? "Create your password" : "Choose a new password"}</h1>
            <p className="text-sm text-slate-600" data-testid="reset-email">for <b>{info.email}</b></p>
            <input type="password" required placeholder="New password" value={pw} onChange={e => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password" className={inputCls} />
            <input type="password" required placeholder="Confirm password" value={pw2} onChange={e => setPw2(e.target.value)} autoComplete="new-password" data-testid="reset-password-confirm" className={inputCls} />
            <ul className="space-y-1 text-xs">
              {[...RULES, { t: "Passwords match", ok: () => pw && pw === pw2 }].map(r => (
                <li key={r.t} className={`flex items-center gap-2 ${r.ok(pw) ? "text-emerald-600" : "text-slate-400"}`}>
                  {r.ok(pw) ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />} {r.t}
                </li>
              ))}
            </ul>
            <button disabled={!valid || saving} className="ae-btn-primary w-full disabled:opacity-50" data-testid="reset-submit">{saving ? "Saving…" : "Save Password"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
